/**
 * JSON-Render Test Page
 *
 * Test page for trying out the json-render chat UI integration.
 * Access at /test/json-render
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Renderer } from '@json-render/react';
import { Send, Trash2, Code, Eye, Loader2, RefreshCw } from 'lucide-react';

import { componentRegistry } from '../components/json-render/registry';
import { SimpleCommerceProvider } from '../components/json-render/Providers';
import { useJsonRenderStream } from '../hooks/useJsonRenderStream';

// Example queries for quick testing
const EXAMPLE_QUERIES = [
  'Show me Nike running shoes under $150',
  'I need a waterproof jacket for hiking',
  'Compare the best wireless headphones',
  'Find me sustainable sneakers',
  'What are the top rated backpacks?',
  'Show me luxury watches',
];

export default function JsonRenderTestPage() {
  const [input, setInput] = useState('');
  const [sessionId] = useState(() => `test_${Date.now()}`);
  const [showJson, setShowJson] = useState(false);
  const [actionLog, setActionLog] = useState([]);
  const inputRef = useRef(null);

  // Use the streaming hook with public test endpoint
  const { tree, isStreaming, error, progress, send, clear } = useJsonRenderStream({
    sessionId,
    baseUrl: '', // Uses relative URL
    usePublicEndpoint: true, // Use /api/test/stream for unauthenticated testing
    onError: (err) => console.error('Stream error:', err),
    onComplete: () => console.log('Stream complete'),
  });

  // Handle form submit
  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (!input.trim() || isStreaming) return;

    console.log('[Test] Sending:', input);
    send(input, { cartItemCount: 0, preferences: {} });
    setInput('');
  }, [input, isStreaming, send]);

  // Handle example click
  const handleExampleClick = useCallback((query) => {
    setInput(query);
    inputRef.current?.focus();
  }, []);

  // Handle actions from components
  const handleAction = useCallback((action) => {
    console.log('[Test] Action:', action);
    setActionLog((prev) => [
      { ...action, timestamp: new Date().toISOString() },
      ...prev.slice(0, 9), // Keep last 10
    ]);

    // Handle refine_search by sending new query
    if (action.name === 'refine_search' && action.params?.query) {
      send(action.params.query, { cartItemCount: 0, preferences: {} });
    }

    return { success: true };
  }, [send]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const hasTree = tree && Object.keys(tree.elements || {}).length > 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                JSON-Render Test Page
              </h1>
              <p className="text-sm text-gray-500">
                Test the AI-generated UI components
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowJson(!showJson)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm ${
                  showJson
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {showJson ? <Eye size={16} /> : <Code size={16} />}
                {showJson ? 'View UI' : 'View JSON'}
              </button>
              <button
                onClick={clear}
                disabled={!hasTree && !isStreaming}
                className="flex items-center gap-2 px-3 py-1.5 rounded text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
              >
                <Trash2 size={16} />
                Clear
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main content area */}
          <div className="lg:col-span-2 space-y-4">
            {/* Input form */}
            <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask about products..."
                  disabled={isStreaming}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-penta-mint focus:border-transparent disabled:bg-gray-50"
                />
                <button
                  type="submit"
                  disabled={!input.trim() || isStreaming}
                  className="flex items-center gap-2 px-4 py-2 bg-penta-mint text-white rounded-lg hover:bg-penta-mint/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isStreaming ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <Send size={18} />
                  )}
                  Send
                </button>
              </div>

              {/* Example queries */}
              <div className="mt-3 flex flex-wrap gap-2">
                {EXAMPLE_QUERIES.map((query, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => handleExampleClick(query)}
                    className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                  >
                    {query}
                  </button>
                ))}
              </div>
            </form>

            {/* Status bar */}
            {(isStreaming || progress) && (
              <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm">
                <Loader2 size={16} className="animate-spin" />
                {progress || 'Processing...'}
              </div>
            )}

            {/* Error display */}
            {error && (
              <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
                <p className="font-medium">Error</p>
                <p className="text-sm">{error.message}</p>
                <button
                  onClick={clear}
                  className="mt-2 flex items-center gap-1 text-sm text-red-600 hover:underline"
                >
                  <RefreshCw size={14} />
                  Try again
                </button>
              </div>
            )}

            {/* Results area */}
            <div className="bg-white rounded-lg border border-gray-200 min-h-[400px]">
              {showJson ? (
                /* JSON view */
                <div className="p-4">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">JSON Tree</h3>
                  {hasTree ? (
                    <pre className="text-xs bg-gray-900 text-green-400 p-4 rounded overflow-auto max-h-[500px]">
                      {JSON.stringify(tree, null, 2)}
                    </pre>
                  ) : (
                    <p className="text-gray-400 text-sm">No tree yet. Send a message to generate UI.</p>
                  )}
                </div>
              ) : (
                /* Rendered UI view */
                <div className="p-4">
                  {hasTree ? (
                    <SimpleCommerceProvider sessionId={sessionId} onAction={handleAction}>
                      <Renderer
                        tree={tree}
                        registry={componentRegistry}
                        loading={isStreaming}
                      />
                    </SimpleCommerceProvider>
                  ) : !isStreaming ? (
                    <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                      <p>Send a message to see AI-generated product UI</p>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Session info */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Session Info</h3>
              <dl className="text-sm space-y-1">
                <div className="flex justify-between">
                  <dt className="text-gray-500">Session ID:</dt>
                  <dd className="font-mono text-xs text-gray-700">{sessionId.slice(0, 15)}...</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Status:</dt>
                  <dd className={isStreaming ? 'text-blue-600' : 'text-green-600'}>
                    {isStreaming ? 'Streaming' : 'Ready'}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500">Elements:</dt>
                  <dd className="text-gray-700">{Object.keys(tree?.elements || {}).length}</dd>
                </div>
              </dl>
            </div>

            {/* Action log */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Action Log</h3>
              {actionLog.length > 0 ? (
                <div className="space-y-2 max-h-[300px] overflow-auto">
                  {actionLog.map((action, i) => (
                    <div key={i} className="text-xs bg-gray-50 rounded p-2">
                      <div className="font-medium text-gray-700">{action.name}</div>
                      <pre className="text-gray-500 mt-1 overflow-auto">
                        {JSON.stringify(action.params, null, 2)}
                      </pre>
                      <div className="text-gray-400 mt-1">
                        {new Date(action.timestamp).toLocaleTimeString()}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400">
                  Click buttons in the UI to see actions logged here
                </p>
              )}
            </div>

            {/* Component list */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Available Components</h3>
              <ul className="text-xs space-y-1">
                {Object.keys(componentRegistry).map((name) => (
                  <li key={name} className="text-gray-600">
                    • {name}
                  </li>
                ))}
              </ul>
            </div>

            {/* Instructions */}
            <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
              <h3 className="text-sm font-medium text-blue-800 mb-2">How to Test</h3>
              <ol className="text-xs text-blue-700 space-y-1 list-decimal list-inside">
                <li>Type a product query or click an example</li>
                <li>Watch the streaming response render</li>
                <li>Click product buttons to trigger actions</li>
                <li>Toggle JSON view to see the tree structure</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
